import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Alert,
  AlertTitle,
  IconButton,
  Collapse,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip
} from '@mui/material';
import {
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import therapistApi from '../../services/therapistApi';
import therapistStyles from './styles/therapistStyles';

// Above this many alerts the banner starts collapsed. One alert is not a wall,
// so collapsing it buys nothing and costs a click.
const COLLAPSE_ABOVE = 1;

const isSevere = (alert) => alert.priority === 'critical' || alert.priority === 'urgent';

/**
 * CareNoteAlerts — the crisis surface, above every therapist view.
 *
 * It used to render every pending alert at full size, one under another. With
 * five pending that was 479px of a 900px screen: correct information, but it
 * pushed the entire rest of the dashboard below the fold, so the things a
 * therapist needs *between* crises became unreachable without scrolling.
 *
 * Now a single banner states the count and expands to the same list. Three
 * rules keep the collapse from quietly becoming a way of turning the alarm
 * down, which is the obvious failure mode of summarising a crisis surface:
 *
 *   1. The count is always visible, and separates what still needs action
 *      from what has been acknowledged. Collapsed never means silent.
 *   2. A new alert force-opens the banner. The poll runs every 30s, so an
 *      alert can land while the therapist is looking elsewhere; arriving into
 *      a collapsed banner unseen is exactly what must not happen.
 *   3. The collapsed state is never persisted. A stale "collapsed" preference
 *      from last week must not decide how today's crisis is presented.
 */
function CareNoteAlerts({ token }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [resolveDialog, setResolveDialog] = useState({ open: false, alertId: null });
  const [resolution, setResolution] = useState('');
  // null means the therapist has not chosen yet, so the default applies. Held
  // in state only — deliberately not in localStorage (rule 3 above).
  const [listOpen, setListOpen] = useState(null);
  // Rule 2. null on the first load so the initial batch does not count as
  // "new" and force the banner open before anyone has looked at it.
  const previousPendingCount = useRef(null);

  useEffect(() => {
    loadAlerts();
    // Set up periodic refresh every 30 seconds
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAlerts = async () => {
    try {
      const response = await therapistApi.getPendingAlerts(token);
      const nextAlerts = response.alerts || [];
      setAlerts(nextAlerts);

      const pending = nextAlerts.filter(a => a.status !== 'acknowledged').length;
      if (previousPendingCount.current !== null && pending > previousPendingCount.current) {
        setListOpen(true);
      }
      previousPendingCount.current = pending;
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await therapistApi.acknowledgeAlert(alertId, token);
      await loadAlerts();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const handleResolve = async () => {
    if (!resolveDialog.alertId || !resolution.trim()) return;

    try {
      await therapistApi.resolveAlert(resolveDialog.alertId, resolution, token);
      setResolveDialog({ open: false, alertId: null });
      setResolution('');
      await loadAlerts();
    } catch (error) {
      console.error('Error resolving alert:', error);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await therapistApi.dismissAlert(alertId, token);
      await loadAlerts();
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const toggleExpanded = (alertId) => {
    setExpanded(prev => ({
      ...prev,
      [alertId]: !prev[alertId]
    }));
  };

  if (loading || alerts.length === 0) return null;

  const pendingAlerts = alerts.filter(a => a.status !== 'acknowledged');
  const acknowledgedCount = alerts.length - pendingAlerts.length;
  const criticalPending = pendingAlerts.filter(a => a.priority === 'critical').length;
  const urgentPending = pendingAlerts.filter(a => a.priority === 'urgent').length;
  const severePending = pendingAlerts.some(isSevere);

  // Rule 1: the headline says what is outstanding, not merely how many rows
  // exist. "3 alerts to address" and "3 alerts, all acknowledged" are very
  // different states and must not read the same when collapsed.
  const summaryText = pendingAlerts.length > 0
    ? `${pendingAlerts.length} alert${pendingAlerts.length === 1 ? '' : 's'} to address`
    : `${alerts.length} alert${alerts.length === 1 ? '' : 's'}, all acknowledged`;

  const listVisible = listOpen === null ? alerts.length <= COLLAPSE_ABOVE : listOpen;

  return (
    <Box sx={{ mb: 3 }}>
      <Alert
        severity={severePending ? 'error' : (pendingAlerts.length > 0 ? 'warning' : 'info')}
        icon={<WarningIcon />}
        action={
          <Button
            size="small"
            onClick={() => setListOpen(!listVisible)}
            endIcon={listVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={listVisible}
            aria-label={listVisible ? 'Hide alert details' : `Show all ${alerts.length} alerts`}
            sx={{
              color: 'inherit',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            {listVisible ? 'Hide' : 'Show all'}
          </Button>
        }
        sx={{
          mb: listVisible ? 2 : 0,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: severePending ? 'error.light' : 'warning.light',
          boxShadow: therapistStyles.mainCard.boxShadow,
          '& .MuiAlert-message': { py: 0.5 }
        }}
      >
        <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0 }}>
          {summaryText}
          {criticalPending > 0 && (
            <Chip
              label={`${criticalPending} CRITICAL`}
              size="small"
              sx={{
                backgroundColor: '#d32f2f',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '0.7rem',
                animation: 'pulse 2s infinite'
              }}
            />
          )}
          {urgentPending > 0 && (
            <Chip
              label={`${urgentPending} URGENT`}
              size="small"
              sx={{
                backgroundColor: '#ef5350',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
          )}
          {acknowledgedCount > 0 && (
            <Chip
              label={`${acknowledgedCount} acknowledged`}
              size="small"
              variant="outlined"
              sx={{
                borderColor: '#42A5F5',
                color: '#42A5F5',
                fontWeight: 500,
                fontSize: '0.7rem'
              }}
            />
          )}
        </AlertTitle>
      </Alert>

      {/* The alerts themselves are unchanged — the collapse is the only new
          thing. MUI's Collapse keeps its children mounted, which is what a
          crisis surface should do: the content is present and findable, it is
          only the height that is zero. */}
      <Collapse in={listVisible}>
      {alerts.map((alert) => (
        <Alert
          key={alert._id}
          severity={isSevere(alert) ? 'error' : 'warning'}
          icon={<WarningIcon />}
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="View client page">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (alert.clientId) {
                      navigate('/therapist/dashboard?tab=clients', {
                        state: {
                          selectClient: alert.clientId,
                          timestamp: Date.now() // Add timestamp to force state change detection
                        },
                        replace: false
                      });
                    }
                  }}
                  aria-label="View client page"
                  sx={{
                    color: 'inherit',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => toggleExpanded(alert._id)}
                aria-label={expanded[alert._id] ? 'Collapse alert details' : 'Expand alert details'}
                sx={{
                  color: 'inherit',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                {expanded[alert._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
          sx={{
            mb: 2,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: isSevere(alert) ? 'error.light' : 'warning.light',
            boxShadow: therapistStyles.mainCard.boxShadow
          }}
        >
          <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Care Note Concern - {alert.clientId?.name || alert.clientId?.email || 'Client'}
            {alert.priority === 'critical' && (
              <Chip
                label="CRITICAL"
                size="small"
                sx={{
                  backgroundColor: '#d32f2f',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  animation: 'pulse 2s infinite'
                }}
              />
            )}
            {alert.priority === 'urgent' && (
              <Chip
                label="URGENT"
                size="small"
                sx={{
                  backgroundColor: '#ef5350',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.7rem'
                }}
              />
            )}
            {alert.status === 'acknowledged' && (
              <Chip
                label="ACKNOWLEDGED"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: '#42A5F5',
                  color: '#42A5F5',
                  fontWeight: 500,
                  fontSize: '0.7rem'
                }}
              />
            )}
          </AlertTitle>
          {/* Not every alert carries a concern sub-document (the crisis-number
              guard's SAFETY_ISSUE alerts did not until 8 Sep 2026); an
              unguarded read here blanked the whole therapist dashboard. */}
          <Typography variant="body2">
            <strong>{alert.concern?.concernType || (alert.metadata?.source || 'alert').replace(/_/g, ' ')}:</strong>{' '}
            {alert.concern?.explanation || 'Open the client\'s Alerts tab for details.'}
          </Typography>

          <Collapse in={expanded[alert._id]}>
            <Box sx={{ mt: 2 }}>
              {alert.concern?.suggestedAction && (
                <Typography variant="body2" paragraph>
                  <strong>Suggested Action:</strong> {alert.concern.suggestedAction.replace(/_/g, ' ')}
                </Typography>
              )}
              {alert.concern?.careNoteReference && (
                <Typography variant="body2" paragraph>
                  <strong>Care Note Reference:</strong> {alert.concern.careNoteReference}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Created: {new Date(alert.createdAt).toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {alert.status === 'pending' && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleAcknowledge(alert._id)}
                    sx={{
                      ...therapistStyles.outlinedButton,
                      fontSize: '0.813rem'
                    }}
                  >
                    Acknowledge
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setResolveDialog({ open: true, alertId: alert._id });
                  }}
                  sx={{
                    ...therapistStyles.primaryButton,
                    fontSize: '0.813rem',
                    py: 0.5,
                    px: 2
                  }}
                >
                  Resolve
                </Button>
                <Button
                  size="small"
                  onClick={() => handleDismiss(alert._id)}
                  sx={{
                    color: '#ef5350',
                    fontSize: '0.813rem',
                    '&:hover': {
                      backgroundColor: 'rgba(239,83,80,0.04)'
                    }
                  }}
                >
                  Dismiss
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Alert>
      ))}
      </Collapse>

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialog.open}
        onClose={() => setResolveDialog({ open: false, alertId: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: therapistStyles.mainCard.boxShadow
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          fontWeight: 600
        }}>
          Resolve Alert
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Resolution Notes"
            fullWidth
            multiline
            rows={4}
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            placeholder="Describe how this concern was addressed..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setResolveDialog({ open: false, alertId: null });
              setResolution('');
            }}
            sx={therapistStyles.outlinedButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResolve}
            variant="contained"
            disabled={!resolution.trim()}
            sx={{
              ...therapistStyles.primaryButton,
              py: 0.75,
              px: 3
            }}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CareNoteAlerts;
